import {AppStore} from "lib/redux/store";
import {authActions, authSelectors} from "features/auth/authSlice";
import axios, {AxiosInstance, AxiosRequestConfig} from "axios";
import {identity, mergeDeepRight, path} from "ramda";

const CUSTOM_RETRY_MARKER = '_waRetried';
const RETRYABLE_STATUS_CODES = new Set([401, 403]);

/**
 * we can't just import redux store here because that creates circular dependencies.
 * -> Store a reference here to be set by calling initFsCockpitAxiosBindings() in App.tsx for the first time
 */
const reduxStoreReference: { ref: AppStore | null } = {ref: null};

export function createFsCockpitAuthedAxiosInstance(axiosConfig?: AxiosRequestConfig): AxiosInstance {
    const axiosInstance = axios.create(axiosConfig);

    axiosInstance.interceptors.request.use(
        config => {
            if (reduxStoreReference.ref == null) {
                return config;
            }

            const accessToken = authSelectors.accessToken(reduxStoreReference.ref.getState());
            if (!accessToken) {
                return Promise.reject('no access token');
            }
            return mergeDeepRight(config, {headers: {'Authorization': `Bearer ${accessToken}`}});
        }
    );


    axiosInstance.interceptors.response.use(
        identity,
        failedRequest => {
            if (reduxStoreReference.ref == null) {
                return Promise.reject(failedRequest);
            }

            const responseStatusCode = path<any>(['response', 'status'], failedRequest);
            const isRetryableStatusCode = responseStatusCode != null && RETRYABLE_STATUS_CODES.has(responseStatusCode);
            const alreadyRetried = path(['config', CUSTOM_RETRY_MARKER], failedRequest);
            if (!isRetryableStatusCode || alreadyRetried) {
                return Promise.reject(failedRequest);
            }

            return reduxStoreReference.ref.dispatch(authActions.refresh())
                .then(() => {
                    const newConfig = Object.assign({}, failedRequest.response.config, {CUSTOM_RETRY_MARKER: true});
                    return axios.request(newConfig);
                });
        }
    )

    return axiosInstance;
}

export function initFsCockpitAxiosBindings(reduxStore: AppStore) {
    if (reduxStoreReference.ref != null) {
        throw new Error("double initiation of FS.Cockpit authed Axios instances");
    }

    reduxStoreReference.ref = reduxStore;
}
